import { BriefProgressEnum, BriefResponseDto, BriefStatusEnum, SearchBriefResponseDto } from '@/app/common/general-types/briefs.dto';
import {
  BRIEF_SORTS,
  BRIEF_TABS,
  BRIEF_TYPES,
  BRIEFS_PAGE_SIZE,
  BriefActionKind,
  BriefListItem,
  BriefListQuery,
  BriefSort,
  BriefStatus,
  BriefTab,
  BriefType,
  BriefsListRequest,
  BriefsListResponse,
  DEFAULT_BRIEF_QUERY,
  PRESET_BRIEF_VIEWS,
  SavedBriefView,
} from './briefs-list.types';

const AVATAR_COLORS = ['#DB24BF', '#6508FB', '#0B8F4E', '#E67514', '#1D4ED8', '#0F766E'];
const VIEWS_KEY_PREFIX = 'briefs-v2-views:';

export interface FilterChip {
  group: 'q' | 'type' | 'product' | 'owner' | 'campaign';
  value: string;
  groupLabel: string;
  valueLabel: string;
}

export type EmptyKind = 'first-run' | 'caught-up' | 'no-match' | 'tab-empty';

export interface PageWindow {
  from: number;
  to: number;
  pages: number;
  page: number;
}

export function queryFromParams(params: Readonly<Record<string, string | null | undefined>>): BriefListQuery {
  const tab = asTab(params['tab']);
  const sort = asSort(params['sort']);
  const page = Math.max(1, Math.floor(Number(params['page']) || 1));
  return {
    tab,
    q: params['q']?.trim() ?? '',
    types: splitList(params['type']).filter((value): value is BriefType => BRIEF_TYPES.includes(value as BriefType)),
    products: splitList(params['product']),
    owners: splitList(params['owner']),
    campaign: params['campaign']?.trim() ?? '',
    sort,
    page,
  };
}

export function queryToParams(query: BriefListQuery): Record<string, string | null> {
  return {
    tab: query.tab === DEFAULT_BRIEF_QUERY.tab ? null : query.tab,
    q: query.q.trim() ? query.q.trim() : null,
    type: query.types.length ? query.types.join(',') : null,
    product: query.products.length ? query.products.join(',') : null,
    owner: query.owners.length ? query.owners.join(',') : null,
    campaign: query.campaign.trim() ? query.campaign.trim() : null,
    sort: query.sort === DEFAULT_BRIEF_QUERY.sort ? null : query.sort,
    page: query.page > 1 ? String(query.page) : null,
  };
}

export function sameQuery(left: BriefListQuery, right: BriefListQuery): boolean {
  return JSON.stringify(normalizeQuery(left)) === JSON.stringify(normalizeQuery(right));
}

export function hasActiveFilters(query: BriefListQuery): boolean {
  return query.q.trim().length > 0 || query.types.length > 0 || query.products.length > 0 || query.owners.length > 0 || query.campaign.trim().length > 0;
}

export function toListRequest(query: BriefListQuery, businessAccountId: string, meId: string): BriefsListRequest {
  const page = Math.max(1, query.page);
  return {
    businessAccountId,
    tab: query.tab,
    search: query.q.trim(),
    types: query.types,
    productIds: query.products,
    ownerIds: query.owners.map((ownerId) => (ownerId === 'me' ? meId : ownerId)).filter((ownerId) => ownerId.length > 0),
    campaignId: query.campaign.trim() || null,
    sort: query.sort,
    skip: (page - 1) * BRIEFS_PAGE_SIZE,
    take: BRIEFS_PAGE_SIZE,
  };
}

export function pageWindow(page: number, total: number): PageWindow {
  const pages = Math.max(1, Math.ceil(total / BRIEFS_PAGE_SIZE));
  const safePage = total === 0 ? 1 : Math.min(Math.max(1, page), pages);
  if (total === 0) {
    return { from: 0, to: 0, pages, page: safePage };
  }
  return {
    from: (safePage - 1) * BRIEFS_PAGE_SIZE + 1,
    to: Math.min(safePage * BRIEFS_PAGE_SIZE, total),
    pages,
    page: safePage,
  };
}

export function emptyKind(query: BriefListQuery, response: BriefsListResponse): EmptyKind | null {
  if (response.rows.length > 0) {
    return null;
  }
  if (response.unfilteredTotal === 0) {
    return 'first-run';
  }
  if (query.tab === 'attn' && !hasActiveFilters(query)) {
    return 'caught-up';
  }
  if (hasActiveFilters(query)) {
    return 'no-match';
  }
  return 'tab-empty';
}

export function actionText(kind: BriefActionKind, count: number, brief?: BriefListItem): string {
  switch (kind) {
    case 'approve':
      return `${count} ${count === 1 ? 'video' : 'videos'} to approve`;
    case 'flags':
      return `${count} ${count === 1 ? 'red flag' : 'red flags'} to review`;
    case 'posts':
      return `${count} ${count === 1 ? 'post' : 'posts'} flagged`;
    case 'screen':
      return `${count} ${count === 1 ? 'creator' : 'creators'} to screen`;
    case 'apply':
      return `${count} ${count === 1 ? 'applicant' : 'applicants'} waiting`;
    case 'setup':
      if (brief && (brief.type === 'screening' || brief.type === 'monitoring') && brief.creatorCount === 0) {
        return 'No creators assigned';
      }
      return 'Finish setup';
  }
}

export function actionButton(kind: BriefActionKind, brief?: BriefListItem): { label: string; outline: boolean } {
  switch (kind) {
    case 'approve':
      return { label: 'Approve videos', outline: false };
    case 'flags':
      return { label: 'Review flags', outline: false };
    case 'posts':
      return { label: 'Review posts', outline: false };
    case 'screen':
      return { label: 'Screen creators', outline: false };
    case 'apply':
      return { label: 'Review applicants', outline: false };
    case 'setup':
      if (brief && (brief.type === 'screening' || brief.type === 'monitoring') && brief.creatorCount === 0) {
        return { label: 'Assign Creators', outline: false };
      }
      return { label: 'Continue setup', outline: true };
  }
}

export function overviewCommands(brief: BriefListItem): string[] {
  if (brief.type === 'screening') {
    return ['/manage-brief-screen', brief.id];
  }
  if (brief.type === 'monitoring') {
    return ['/manage-brief-monitor', brief.id];
  }
  return ['/manage-brief', brief.id];
}

export function actionCommands(brief: BriefListItem): string[] | null {
  const kind = brief.pendingAction?.kind;
  if (!kind) {
    return null;
  }
  if (kind === 'setup') {
    if ((brief.type === 'screening' || brief.type === 'monitoring') && brief.creatorCount === 0) {
      return brief.type === 'screening'
        ? ['/manage-brief-screen', brief.id, 'creators']
        : ['/manage-brief-monitor', brief.id, 'creators'];
    }
    return null;
  }
  switch (kind) {
    case 'approve':
      return ['/manage-brief', brief.id, 'moderation'];
    case 'apply':
      return ['/manage-brief', brief.id, 'assignment'];
    case 'flags':
      return ['/manage-brief-screen', brief.id, 'content'];
    case 'screen':
      return ['/manage-brief-screen', brief.id, 'creators'];
    case 'posts':
      return ['/manage-brief-monitor', brief.id, 'content'];
  }
}

export function typeLabel(type: BriefType): string {
  switch (type) {
    case 'ugc':
      return 'UGC Content';
    case 'screening':
      return 'Social Screening';
    case 'monitoring':
      return 'Social Monitoring';
  }
}

export function briefStatusEnum(status: BriefStatus): BriefStatusEnum {
  switch (status) {
    case 'live':
      return BriefStatusEnum.Public;
    case 'paused':
      return BriefStatusEnum.Private;
    case 'completed':
      return BriefStatusEnum.Complete;
    case 'draft':
      return BriefStatusEnum.Draft;
  }
}

export function listStatusFromEnum(status: BriefStatusEnum): BriefStatus | null {
  switch (status) {
    case BriefStatusEnum.Public:
      return 'live';
    case BriefStatusEnum.Private:
      return 'paused';
    case BriefStatusEnum.Complete:
      return 'completed';
    case BriefStatusEnum.Draft:
      return 'draft';
    default:
      return null;
  }
}

export function toSearchBrief(item: BriefListItem): SearchBriefResponseDto {
  return {
    id: item.id,
    title: item.name,
    currentStatus: briefStatusEnum(item.status),
    enableAutoPilot: false,
    campaignImage: '',
    campaignName: '',
    createdDate: new Date(item.updatedAt),
    currentStatusProgress: BriefProgressEnum.Briefing,
    filterStats: {},
    product: { productName: item.product.name, productImage: item.product.image },
    shoutBrief: null,
    currentPrice: null,
    isDeleted: false,
  };
}

export function mergeSearchBrief(item: BriefListItem, full: BriefResponseDto): SearchBriefResponseDto {
  const stub = toSearchBrief(item);
  return {
    ...stub,
    title: full.title || stub.title,
    currentStatus: full.currentStatus?.status ?? stub.currentStatus,
    shoutBrief: full.shoutBrief ?? null,
    currentPrice: full.currentPrice ?? null,
    pieces: full.pieces ?? null,
    videosPerCreator: typeof full.videosPerCreator === 'number' ? full.videosPerCreator : null,
    enableAutoPilot: Boolean(full.enableAutoPilot),
    isReadyForContentReview: full.isReadyForContentReview,
  };
}

export function creatorsText(brief: BriefListItem, fetchedRequired: number | null = null): string {
  if (brief.type === 'ugc') {
    const required = brief.requiredCreators ?? fetchedRequired;
    if (required != null) {
      return `${brief.creatorCount}/${required}`;
    }
  }
  return brief.creatorCount > 0 ? String(brief.creatorCount) : '—';
}

export function creatorsAria(brief: BriefListItem, fetchedRequired: number | null = null): string | null {
  if (brief.type !== 'ugc') {
    return null;
  }
  const required = brief.requiredCreators ?? fetchedRequired;
  if (required == null) {
    return null;
  }
  return `${brief.creatorCount} of ${required} required creators hired`;
}

export function statusLabel(status: BriefStatus): string {
  switch (status) {
    case 'live':
      return 'Live';
    case 'draft':
      return 'Draft';
    case 'paused':
      return 'Paused';
    case 'completed':
      return 'Completed';
  }
}

export function tabLabel(tab: BriefTab): string {
  switch (tab) {
    case 'attn':
      return 'Needs action';
    case 'live':
      return 'Live';
    case 'draft':
      return 'Drafts';
    case 'paused':
      return 'Paused';
    case 'completed':
      return 'Completed';
    case 'all':
      return 'All';
  }
}

export function sortLabel(sort: BriefSort): string {
  switch (sort) {
    case 'updated':
      return 'Recently updated';
    case 'end':
      return 'Ending soonest';
    case 'name':
      return 'Name A–Z';
    case 'creators':
      return 'Most creators';
  }
}

export function ownerLabel(name: string, ownerId: string, meId: string): string {
  return ownerId === meId || ownerId === 'me' ? 'You' : name;
}

export function avatarColor(ownerId: string): string {
  let hash = 0;
  for (const char of ownerId) {
    hash = (hash + char.charCodeAt(0)) % AVATAR_COLORS.length;
  }
  return AVATAR_COLORS[hash] ?? AVATAR_COLORS[0];
}

export function avatarInitial(name: string): string {
  const letter = name.trim().charAt(0);
  return letter ? letter.toUpperCase() : '?';
}

export function updatedLabel(iso: string, now = new Date()): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return '';
  }
  const hours = Math.max(0, Math.round((now.getTime() - then) / 3600000));
  if (hours < 1) {
    return 'Just now';
  }
  if (hours < 24) {
    return `${hours}h ago`;
  }
  if (hours < 48) {
    return 'Yesterday';
  }
  const days = Math.round(hours / 24);
  if (days < 7) {
    return `${days} days ago`;
  }
  const weeks = Math.round(days / 7);
  if (weeks < 5) {
    return `${weeks} wk ago`;
  }
  return `${Math.max(1, Math.round(days / 30))} mo ago`;
}

export function endsDisplay(
  brief: Pick<BriefListItem, 'status' | 'endsAt' | 'endsKind' | 'pausedAt'>,
  now = new Date(),
): { primary: string; secondary: string } {
  if (brief.status === 'paused' && brief.pausedAt) {
    return { primary: '—', secondary: `Paused ${formatDay(brief.pausedAt)}` };
  }
  if (brief.status === 'draft' || brief.endsKind === 'none') {
    return { primary: '—', secondary: '' };
  }
  if (brief.endsKind === 'ongoing' || !brief.endsAt) {
    return { primary: 'Ongoing', secondary: '' };
  }
  const end = new Date(brief.endsAt);
  if (Number.isNaN(end.getTime())) {
    return { primary: '—', secondary: '' };
  }
  if (end.getTime() < now.getTime()) {
    return { primary: formatDay(brief.endsAt), secondary: 'Ended' };
  }
  return { primary: formatDay(brief.endsAt), secondary: futureLabel(end, now) };
}

export function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

export function filterChips(
  query: BriefListQuery,
  response: BriefsListResponse | null,
  meId: string,
  campaignName = '',
): FilterChip[] {
  const chips: FilterChip[] = [];
  if (query.q.trim()) {
    chips.push({ group: 'q', value: '', groupLabel: 'Search', valueLabel: `“${query.q.trim()}”` });
  }
  for (const type of query.types) {
    chips.push({ group: 'type', value: type, groupLabel: 'Type', valueLabel: typeLabel(type) });
  }
  for (const productId of query.products) {
    const match = response?.facets.products.find((facet) => facet.id === productId);
    chips.push({ group: 'product', value: productId, groupLabel: 'Product', valueLabel: match?.name ?? productId });
  }
  if (query.campaign.trim()) {
    chips.push({
      group: 'campaign',
      value: query.campaign,
      groupLabel: 'Campaign',
      valueLabel: campaignName.trim() || 'Campaign',
    });
  }
  for (const ownerId of query.owners) {
    const match = response?.facets.owners.find((facet) => facet.id === ownerId || (ownerId === 'me' && facet.id === meId));
    chips.push({
      group: 'owner',
      value: ownerId,
      groupLabel: 'Owner',
      valueLabel: ownerLabel(match?.name ?? ownerId, ownerId === 'me' ? meId : ownerId, meId),
    });
  }
  return chips;
}

export function matchesTab(brief: BriefListItem, tab: BriefTab): boolean {
  if (tab === 'all') {
    return true;
  }
  if (tab === 'attn') {
    return brief.pendingAction != null && brief.status !== 'completed';
  }
  return brief.status === tab;
}

export function replaceBrief(
  response: BriefsListResponse,
  briefId: string,
  next: BriefListItem,
  tab: BriefTab,
): BriefsListResponse {
  const stays = matchesTab(next, tab);
  const existed = response.rows.some((row) => row.id === briefId);
  if (!existed) {
    return response;
  }
  return {
    ...response,
    rows: stays
      ? response.rows.map((row) => (row.id === briefId ? next : row))
      : response.rows.filter((row) => row.id !== briefId),
    total: stays ? response.total : Math.max(0, response.total - 1),
  };
}

export function removeBrief(response: BriefsListResponse, briefId: string): BriefsListResponse {
  const existed = response.rows.some((row) => row.id === briefId);
  if (!existed) {
    return response;
  }
  return {
    ...response,
    rows: response.rows.filter((row) => row.id !== briefId),
    total: Math.max(0, response.total - 1),
    unfilteredTotal: Math.max(0, response.unfilteredTotal - 1),
  };
}

export function readSavedViews(userId: string): SavedBriefView[] {
  try {
    const raw = localStorage.getItem(viewsKey(userId));
    if (!raw) {
      return [];
    }
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.filter(isSavedView);
  } catch {
    return [];
  }
}

export function writeSavedViews(userId: string, views: SavedBriefView[]): void {
  localStorage.setItem(viewsKey(userId), JSON.stringify(views));
}

export function applySavedView(current: BriefListQuery, view: SavedBriefView): BriefListQuery {
  return {
    ...current,
    tab: view.tab,
    q: '',
    types: [...view.types],
    products: [...view.products],
    owners: [...view.owners],
    page: 1,
  };
}

export function presetViews(): SavedBriefView[] {
  return PRESET_BRIEF_VIEWS.map((view) => ({ ...view, types: [...view.types], products: [...view.products], owners: [...view.owners] }));
}

function viewsKey(userId: string): string {
  return `${VIEWS_KEY_PREFIX}${userId || 'anon'}`;
}

function isSavedView(value: unknown): value is SavedBriefView {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const view = value as SavedBriefView;
  return typeof view.id === 'string' && typeof view.name === 'string' && BRIEF_TABS.includes(view.tab);
}

function asTab(value: string | null | undefined): BriefTab {
  return BRIEF_TABS.includes(value as BriefTab) ? (value as BriefTab) : DEFAULT_BRIEF_QUERY.tab;
}

function asSort(value: string | null | undefined): BriefSort {
  return BRIEF_SORTS.includes(value as BriefSort) ? (value as BriefSort) : DEFAULT_BRIEF_QUERY.sort;
}

function splitList(value: string | null | undefined): string[] {
  if (!value) {
    return [];
  }
  return [...new Set(value.split(',').map((item) => item.trim()).filter((item) => item.length > 0))];
}

function normalizeQuery(query: BriefListQuery): BriefListQuery {
  return {
    ...query,
    q: query.q.trim(),
    types: [...query.types],
    products: [...query.products],
    owners: [...query.owners],
    campaign: query.campaign.trim(),
  };
}

function futureLabel(end: Date, now: Date): string {
  const days = Math.max(1, Math.round((end.getTime() - now.getTime()) / 86400000));
  if (days < 14) {
    return days === 1 ? 'in 1 day' : `in ${days} days`;
  }
  const weeks = Math.round(days / 7);
  if (weeks < 8) {
    return weeks === 1 ? 'in 1 week' : `in ${weeks} weeks`;
  }
  const months = Math.max(1, Math.round(days / 30));
  return months === 1 ? 'in 1 month' : `in ${months} months`;
}
